import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useProfile } from '../../hooks';
import { Button, Input, Card, CardHeader, CardBody, Avatar, Alert } from '../../components/ui';
import { PhotoCropModal } from '../../components';

const INDUSTRY_OPTIONS = [
  'Aged Care',
  'Disability Support',
  'Mental Health',
  'Child Care',
  'Community Services',
  'Hospital / Clinical',
  'Home Care',
  'Palliative Care',
  'Allied Health',
  'Other',
];

const COMPANY_SIZE_OPTIONS = [
  '1–10 employees',
  '11–50 employees',
  '51–200 employees',
  '201–500 employees',
  '500+ employees',
];

export const EditManagerProfile: React.FC = () => {
  const { user } = useAuth();
  const { profile, loading, error, updateManagerProfile, uploadPhoto, deletePhoto, clearError } = useProfile();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    company_name: '',
    industry: '',
    company_size: '',
    company_website: '',
  });
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [cropSource, setCropSource] = useState<{ src: string; fileName: string } | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        company_name: profile.company_name || '',
        industry: profile.industry || '',
        company_size: profile.company_size || '',
        company_website: profile.company_website || '',
      });
      setPhotoPreview(profile.photo_url ?? null);
    }
  }, [profile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const clearFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const src = URL.createObjectURL(file);
    setCropSource({ src, fileName: file.name });
  };

  const handleCropCancel = () => {
    if (cropSource) URL.revokeObjectURL(cropSource.src);
    setCropSource(null);
    clearFileInput();
  };

  const handleCropComplete = async (file: File, previewUrl: string) => {
    if (cropSource) URL.revokeObjectURL(cropSource.src);
    setCropSource(null);
    clearFileInput();
    setPhotoPreview(previewUrl);
    await uploadPhoto(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);
    const payload = { ...formData };
    if (payload.company_website && !/^https?:\/\//i.test(payload.company_website)) {
      payload.company_website = `https://${payload.company_website}`;
    }
    const result = await updateManagerProfile(payload);
    if (result?.success) {
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1500);
    }
  };

  return (
    <div>
      {cropSource && (
        <PhotoCropModal
          isOpen
          imageSrc={cropSource.src}
          fileName={cropSource.fileName}
          title="Adjust logo"
          onCancel={handleCropCancel}
          onComplete={handleCropComplete}
        />
      )}
      <Card collapsible={false}>
        <CardHeader>
          <h1 className="text-2xl font-bold">Manager Profile</h1>
        </CardHeader>
        <CardBody>
          {error && (
            <Alert variant="danger" onClose={clearError} className="mb-4">{error}</Alert>
          )}
          {success && (
            <Alert variant="success" className="mb-4">Profile updated successfully!</Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Logo / Photo */}
            <div className="flex flex-col items-center">
              <Avatar
                src={photoPreview}
                alt="Company Logo"
                size="xl"
                fallback={formData.company_name?.charAt(0) || user?.email?.charAt(0)?.toUpperCase()}
                className="mb-3"
              />
              <div className="flex gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                  Upload Logo
                </Button>
                {photoPreview && (
                  <Button type="button" variant="danger" size="sm" onClick={() => { deletePhoto(); setPhotoPreview(null); }}>
                    Remove
                  </Button>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
            </div>

            <div className="border-t border-gray-100 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Company Details</h3>
              <div className="space-y-4">
                <Input
                  label="Company Name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Sunrise Care Group"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">Select industry</option>
                    {INDUSTRY_OPTIONS.map(i => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Size</label>
                  <select
                    name="company_size"
                    value={formData.company_size}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">Select size</option>
                    {COMPANY_SIZE_OPTIONS.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <Input
                  label="Company Website"
                  name="company_website"
                  type="text"
                  value={formData.company_website}
                  onChange={handleChange}
                  placeholder="e.g. wandawazi.com"
                />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Contact Person</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  autoComplete="given-name"
                />
                <Input
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  autoComplete="family-name"
                />
              </div>
              <div className="mt-4">
                <Input
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  autoComplete="tel"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button type="submit" loading={loading} fullWidth className="sm:w-auto">
                Save Profile
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate('/dashboard')} fullWidth className="sm:w-auto">
                Cancel
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
};
